<script setup>
import _ from 'lodash';
import { computed, ref, watch, getCurrentInstance, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import IconBase from '../icon/IconBase.vue';
import IconCamera from '../icon/IconCamera.vue';
import IconFolder from '../icon/IconFolder.vue';
import IconPlaylist from '../icon/IconPlaylist.vue';
import IconFilm from '../icon/IconFilm.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import IconChevronUpDown from '../icon/IconChevronUpDown.vue';
import IconChevronDown from '../icon/IconChevronDown.vue';
import StreamsWidget from './StreamsWidget.vue';
import { useMatchMedia } from '@/utils'

const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const props = defineProps({
  selectedStream: {
    type: Object,
    required: false
  },
  dropRight: {
    type: Boolean,
    required: false,
    default: false
  },
  showPlaylist: {
    type: Boolean,
    default: false
  },
  hideNotification: {
    type: Boolean,
    default: false
  },
  closeAfterSelect: {
    type: Boolean,
    default: false
  },
  onlyLiveSelector: {
    type: Boolean,
    default: false
  },
  refLink: {
    type: Number,
    default: null,
    required: false
  },
  dropdownDisabled: {
    type: Boolean,
    default: false
  },
  source: {
    type: String,
    default: 'paywall'
  },
  hideLive: {
    type: Boolean,
    default: false
  },
  disabledStreams: {
    type: [String, Array],
    default: null
  },
  placeholder: {
    type: String,
    default: 'Select source'
  }
})

const emit = defineEmits({
  'on-show': () => true,
  'on-hide': () => true,
  'on-source-selected': () => true,
})

// A single VOD file can arrive in two shapes:
//  - freshly selected from the widget: a raw VOD file with `fileName` (no `streams`)
//  - returned by the paywall API: { type: 'vod', sourceType: 'vod-file', vodFile: {...} }
const isSingleVodFile = (stream) => {
  if (!stream) return false
  if (stream.sourceType === 'vod-file' || stream.vodFile) return true
  return !!stream.fileName && !stream.streams
}

const singleVodFileName = (stream) => {
  if (!stream) return ''
  // DVR / Live-to-VOD clips carry their name in `vodFile.name` (not `fileName`).
  return stream.vodFile?.fileName || stream.vodFile?.name || stream.fileName || ''
}

const selectedSourceIcon = computed(() => {
  if (isSingleVodFile(props.selectedStream)) return IconFilm
  if (props.selectedStream?.type === 'live') return IconCamera
  if (props.selectedStream?.type === 'vod') return IconFolder
  if (props.selectedStream?.folder?.type === 'vod') return IconFolder
  if (props.source === 'dynamic' && props.selectedStream?.bytes) return IconFilm
  if (props.source === 'mediaflow' && props.selectedStream?.bytes) return IconFilm
  if (props.selectedStream?.streams) return IconPlaylist
  return
})

const normalizedDisabledStreamIds = computed(() => {
  if (!props.disabledStreams) return [];
  return Array.isArray(props.disabledStreams) ? props.disabledStreams.filter(Boolean) : [props.disabledStreams].filter(Boolean);
})

const isDesktop = useMatchMedia('(min-width: 768px)');
const isMultiviewMobile = computed(() => props.source === 'multiview' && !isDesktop.value)

const sourceDropdown = ref(null)
const streamsWidget = ref(null)
const rootEl = ref(null)

// The dropdown opens downward from the field, which can sit low in a modal.
// Measure the real space below the field on open so the list never runs off-screen.
const availableHeight = ref(0)

const wrapperMaxHeightStyle = computed(() =>
  availableHeight.value ? { maxHeight: `${availableHeight.value}px` } : {}
)

const measureAvailableHeight = () => {
  if (!rootEl.value) return
  const rect = rootEl.value.getBoundingClientRect()
  const margin = 16
  const space = window.innerHeight - rect.bottom - margin
  availableHeight.value = Math.max(200, Math.min(space, 560))
}

const onDropdownToggle = async (visible) => {
  if (visible) {
    emit('on-show')
    await nextTick()
    measureAvailableHeight()
    return
  }
  emit('on-hide')
}

const handleWindowResize = _.debounce(() => measureAvailableHeight(), 100)

onMounted(() => window.addEventListener('resize', handleWindowResize))
onBeforeUnmount(() => window.removeEventListener('resize', handleWindowResize))

const selectedVodCheckbox = ref(false)
const sourceSelectedName = ref(props.placeholder)

// Toggle styling: readable selected text, muted placeholder when nothing is chosen.
const toggleButtonClasses = computed(() =>
  `w-full text-tiny h-8 bg-surface-3 hover:bg-surface-4 transition-colors rounded-md flex items-center px-3 truncate disabled:text-surface-7 ${props.selectedStream ? 'text-surface-text-1' : 'text-surface-7'}`
)

const onStreamSourceSelected = (stream, type) => {
  sourceSelectedName.value = stream.name

  if (stream?.file?.fileName) { // should be VOD item
    sourceSelectedName.value = stream.file.fileName
  }

  if (isSingleVodFile(stream)) { // single VOD file selection
    sourceSelectedName.value = singleVodFileName(stream)
  }

  if (sourceDropdown.value) sourceDropdown.value.toggleDropdown()
  if (props.refLink || props.refLink === 0) {
    stream.refLink = props.refLink
  }
  // `type` is the picker tab the pick came from (livestream | vod | playlist);
  // consumers that need to disambiguate shapes can read it.
  emit('on-source-selected', stream, type)
  selectedVodCheckbox.value = false
}

const closeDropdown = () => {
  if(sourceDropdown.value) {
    sourceDropdown.value.hideDropDownMenu()
  }
  emit('on-hide')
}

//Hooks
watch(() => props.selectedStream, () => {
  if (!props.selectedStream) {
    sourceSelectedName.value = props.placeholder
    return
  }
  // Single VOD file takes priority: its `name` is the parent folder, so prefer
  // the actual file name from `vodFile.fileName` (API shape) or `fileName`.
  if (isSingleVodFile(props.selectedStream)) {
    sourceSelectedName.value = singleVodFileName(props.selectedStream)
    return
  }
  if (props.selectedStream?.fileName) sourceSelectedName.value = props.selectedStream?.fileName
  if (props.selectedStream?.name) sourceSelectedName.value = props.selectedStream?.name
}, { immediate: true })


const onMultiviewMobileSelect = () => {
  if (props.dropdownDisabled) return
  emit('on-show')
  streamsWidget.value?.toggleView?.()
}

defineExpose({ closeDropdown })

</script>
<template>
  <div ref="rootEl" class="flex justify-between items-center w-full">
    <drop-down-menu :disabled="dropdownDisabled" v-if="isDesktop" ref="sourceDropdown" class="w-full"
      :customClasses="toggleButtonClasses" @toggle="onDropdownToggle"
      fullWidth="true" position="right" preventHide maxHeight="[70vh]">
      <template #toggle-button>
        <icon-base v-if="selectedSourceIcon" class="!mr-2 shrink-0">
          <component :is="selectedSourceIcon" />
        </icon-base>
        <span class="truncate">{{ sourceSelectedName }}</span>
        <icon-base class="!ml-auto shrink-0">
          <icon-chevron-up-down />
        </icon-base>
      </template>
      <template #menu-items>
        <drop-down-menu-item isCustom class="!px-3 !pt-3 !pb-3">
          <!-- TODO Stream Widget should always have wrapper ID and placement prop = body!
           This might have big problems if not exist. We need to refctore it better in the future -->
          <div id="#streams-widget-wrapper" class="overflow-y-auto overflow-x-hidden" :style="wrapperMaxHeightStyle">
            <StreamsWidget placement="body" @add-stream="onStreamSourceSelected"
              :multiview="(refLink || refLink === 0) ? true : false" :only-live="onlyLiveSelector" :hideLive="hideLive"
              :source="props.source" :disabled-stream-ids="normalizedDisabledStreamIds"
              :selected-id="props.selectedStream?._id" :header-title="placeholder" />
          </div>
        </drop-down-menu-item>
      </template>
    </drop-down-menu>
    <!-- Multiview mobile: look like the desktop select control; StreamsWidget still opens its sheet. -->
    <div
      v-else-if="isMultiviewMobile"
      id="#streams-widget-wrapper"
      role="button"
      tabindex="0"
      class="relative flex h-9 w-full cursor-pointer items-center gap-2 rounded-md bg-surface-3 px-3.5 text-sm transition-colors hover:bg-surface-4"
      :class="[
        dropdownDisabled ? 'pointer-events-none opacity-60' : '',
        selectedStream ? 'text-surface-text-1' : 'text-surface-8',
      ]"
      @click="onMultiviewMobileSelect"
      @keydown.enter.prevent="onMultiviewMobileSelect"
      @keydown.space.prevent="onMultiviewMobileSelect"
    >
      <icon-base v-if="selectedSourceIcon" class="shrink-0">
        <component :is="selectedSourceIcon" />
      </icon-base>
      <span class="min-w-0 flex-1 truncate">{{ sourceSelectedName }}</span>
      <icon-base class="ml-auto shrink-0 text-surface-8">
        <icon-chevron-down />
      </icon-base>
      <!-- Hidden trigger host: keep StreamsWidget mounted, hide its + button via showPlusButton. -->
      <StreamsWidget
        ref="streamsWidget"
        placement="body"
        :show-plus-button="false"
        @add-stream="onStreamSourceSelected"
        :multiview="(refLink || refLink === 0) ? true : false"
        :only-live="onlyLiveSelector"
        :hideLive="hideLive"
        :hide-notification="hideNotification"
        :close-after-select="closeAfterSelect"
        :source="props.source"
        :disabled-stream-ids="normalizedDisabledStreamIds"
        :selected-id="props.selectedStream?._id"
        :header-title="placeholder"
      />
    </div>
    <div v-else id="#streams-widget-wrapper" class="flex items-center w-full">
      <icon-base class="!mr-2 shrink-0">
        <component :is="selectedSourceIcon" />
      </icon-base>
      <span class="truncate mr-auto">{{ sourceSelectedName }}</span>
      <StreamsWidget placement="body" @add-stream="onStreamSourceSelected"
        :multiview="(refLink || refLink === 0) ? true : false" :only-live="onlyLiveSelector" :hideLive="hideLive"
        :hide-notification="hideNotification" :close-after-select="closeAfterSelect" :source="props.source"
        :disabled-stream-ids="normalizedDisabledStreamIds" :selected-id="props.selectedStream?._id"
        :header-title="placeholder" />
    </div>
  </div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* The wrapper is the single scroll container; let the inner paginated lists grow
   naturally so we don't get a nested/double scrollbar that hides the bottom items. */
[id="#streams-widget-wrapper"] :deep(.paginated-list > ul) {
  max-height: none;
  overflow: visible;
}
</style>